import { Component, Inject, OnInit, ViewChild, signal } from '@angular/core';
import { AbstractControl, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentfulMapperService, IsbExperience } from '@nationwide/dgs-angular-billing-common';
import { EasternTimeDatePipe } from '@nationwide/internet-servicing-angular-pipes';
import { environment } from '../../../../../../../environments/environment';
import { PaymentItem } from '../../../../../../billing/payment-preferences/shared/models/payment-item.model';
import { FormUtil } from '../../../../../../shared/payments/form-util';
import { PaymentFormGroup } from '../../../../../../shared/payments/payment-form-group';
import { PaymentMethodsData } from '../../../../../../shared/service/billing-api-common.service';
import { BillingEventService } from '../../../../../../shared/service/ebi-service/billing-events.service';
import { EBIEventType } from '../../../../../../shared/service/ebi-service/ebi-model';
import { SessionService } from '../../../../../../shared/session/session.service';
import { UrlUtil } from '../../../../../../shared/url-util/url-util.service';
import { EasyPayAdapter } from '../../../../shared/adapters/easy-pay.adpater';
import { PaymentMethodsAdapter } from '../../../../shared/adapters/payment-methods.adapter';
import { PaymentPreferencesApiService } from '../../../../shared/services/payment-preferences-api/payment-preferences-api.service';
import { ManagePaymentPrefsFormBuilder } from '../../../base/manage-payment-prefs-form-builder';
import { ManagePaymentPrefsAdapter } from '../../../shared/adapters/manage-payment-prefs-adapter';
import { ManagePaymentPreferencesPageTitle } from '../../../shared/models/manage-payment-preferences-page-title.constant';
import { EditAutomaticPaymentFlowService } from '../services/edit-automatic-payments-flow-service';
import { PayPlanAdapter } from '../../pay-plan/shared/pay-plan.adapter';
import { BillingCommonComponent } from '../../../../../../billing/billing-common/billing-common.component';
import { CommonValuesPaymentFormGroupModel } from '../../../../../../billing/model/common-values-payment-form-group.model';
import { LoggerService } from '../../../../../../shared/logger/logger.service';
import { EftAuthorizationFlowService } from '../../../../../../shared/payments/ez-pay-authorization/eft-authorization-flow.service';
import { EftAuthorizationFormHelperService } from '../../../../../../shared/payments/ez-pay-authorization/eft-authorization-form-helper.service';
import { DistributedDocumentsResponseModel } from '../../../../../../shared/payments/ez-pay-authorization/eft-authorization-form/model/distributed-documents-response.model';
import { AccountPolicyHolderAddressElements } from './../../../../../../shared/payments/ez-pay-authorization/eft-authorization-form/form-elements/account-policy-holder-address-elements';
import { BankAccountPaymentItem } from '../../../../shared/models/bankaccount-item.model';
import { BankCardPaymentItem } from '../../../../shared/models/bankcard-item.model';
import { CelebrusTrackingService } from '../../../../../../shared/service/celebrus-tracking/celebrus-tracking.service';
import { WaitMessageComponent, InterpolationComponent } from '@nationwide/internet-servicing-angular-components';
import { FocusOnInitDirective } from '../../../../../../shared/attr-directives/focus-on-init.directive';
import { PaymentSubHeaderComponent } from '../../../../../../shared/payments/payment-sub-header/payment-sub-header.component';
import { NgClass } from '@angular/common';
import { PaymentMethodSectionComponent } from '../../../../../../shared/payments/payment-method-section/payment-method-section.component';
import { ConsiderThisComponent } from '../../../../../../shared/payments/consider-this/consider-this.component';

@Component({
    selector: 'app-edit-automatic-payments-landing',
    templateUrl: './edit-automatic-payments-landing.component.html',
    styleUrls: ['./edit-automatic-payments-landing.component.scss'],
    providers: [],
    imports: [WaitMessageComponent, FormsModule, ReactiveFormsModule, FocusOnInitDirective, InterpolationComponent, PaymentSubHeaderComponent, NgClass, PaymentMethodSectionComponent, ConsiderThisComponent]
})
export class EditAutomaticPaymentsLandingComponent extends BillingCommonComponent implements OnInit {
    @ViewChild(FormGroupDirective) editPaymentFormDirective: FormGroupDirective;
    declare accountValidationMessage: string;
    pageName = ManagePaymentPreferencesPageTitle.automaticPayments.edit.landing;
    loadComplete = signal(false);
    apiCallInProgress = signal(true);
    paymentItem: PaymentItem;
    editPaymentForm: PaymentFormGroup;
    automaticPayments: IsbExperience.GetPayPlanResponse;
    emailAddress: string;
    paymentMethod: PaymentItem;
    chosenPaymentMethod: PaymentItem;
    billingAccountNumber: string;
    billingAccountHolderName: string;
    amountDue: string;
    nextBillDueDate: Date;
    currentBillDueDate: Date;
    formValidated = false;
    automaticPaymentSuspended: boolean;
    isEFTBilled: boolean;
    isRBCBilled: boolean;
    isBilledStatus: boolean;
    billingStreet1: string;
    billingCity: string;
    billingState: string;
    NBP: boolean;
    isLifeOnlyAccount: boolean;
    declare distributedDocumentsResponseModel: DistributedDocumentsResponseModel;
    declare commonValuesPaymentFormGroupModel: CommonValuesPaymentFormGroupModel;
    canPayNow: boolean;
    hasCreditCardVulPolicyError = signal(false);
    vulErrorMessage = signal('Credit cards cannot be used for variable life policies. Please use a different payment method.');
    bankCardAutomaticPaymentsCapMessage: string;
    isAutoPayment = true;
    paymentPlan: string;
    isFullPay: boolean;
    canCancelOrSuspendAutomaticPayments: boolean;
    isPaymentProcessingWindow: boolean;

    constructor(
        router: Router,
        private activatedRoute: ActivatedRoute,
        session: SessionService,
        private urlUtil: UrlUtil,
        private easyPayAdapter: EasyPayAdapter,
        private paymentPrefsApiService: PaymentPreferencesApiService,
        private editPaymentFormBuilder: ManagePaymentPrefsFormBuilder,
        private paymentMethodsAdapter: PaymentMethodsAdapter,
        private formUtils: FormUtil,
        private easternTime: EasternTimeDatePipe,
        eventService: BillingEventService,
        @Inject('window') private window: Window,
        private managePaymentPrefsAdapter: ManagePaymentPrefsAdapter,
        private editPaymentFlow: EditAutomaticPaymentFlowService,
        private payPlanAdapter: PayPlanAdapter,
        eftAuthorizationFormHelperService: EftAuthorizationFormHelperService,
        eftAuthorizationFlowService: EftAuthorizationFlowService,
        readonly contentfulService: ContentfulMapperService,
        private readonly celebrusTrackingService: CelebrusTrackingService,
        @Inject('logger') private logger: LoggerService
    ) {
        super(session, router, eftAuthorizationFormHelperService, eventService, eftAuthorizationFlowService);
    }

    ngOnInit(): void {
        this.session.currentPage = this.pageName;
        this.setVariablesFromApiResponse();
        this.celebrusTrackingService.initializeCelebrusSessionStorage();
        this.fetchBankCardAutomaticPaymentsCapMessage();
    }

    setVariablesFromApiResponse(): void {
        try {
            this.nextSub = this.paymentPrefsApiService.editAutomaticPaymentLanding({
                callContentful: this.editPaymentFlow.hasCreditCardVulPolicyError
            }).subscribe((response) => {
                const { retrieveCustomerAgreement, internetRegistrationResponse, automaticPayments, paymentsHistory } = response;
                const billingAgreement = this.easyPayAdapter.getBillingAgreementFromRCA(this.easyPayAdapter.getAgreementsFromRCA(retrieveCustomerAgreement));
                this.automaticPayments = automaticPayments;
                this.NBP = this.session.isNBP;
                this.isLifeOnlyAccount = this.managePaymentPrefsAdapter.hasLifeOnlyBusiness(retrieveCustomerAgreement);
                this.isBilledStatus = this.easyPayAdapter.getBilledStatus(billingAgreement);
                this.isEFTBilled = this.easyPayAdapter.getEFTBilledStatus(billingAgreement);
                this.isRBCBilled = this.easyPayAdapter.getRBCBilledStatus(billingAgreement);
                this.amountDue = this.managePaymentPrefsAdapter.fetchBillingAccountAmountDue(retrieveCustomerAgreement);
                this.nextBillDueDate = this.easyPayAdapter.nextDueDate(retrieveCustomerAgreement) ? new Date(this.easyPayAdapter.nextDueDate(retrieveCustomerAgreement)) : null;
                this.currentBillDueDate = this.managePaymentPrefsAdapter.fetchBillingAccountAmountDueDate(retrieveCustomerAgreement);
                this.billingAccountHolderName = this.easyPayAdapter.getFullName(automaticPayments);
                this.billingAccountNumber = this.managePaymentPrefsAdapter.fetchBillingAccountNumber(retrieveCustomerAgreement);
                this.emailAddress = this.managePaymentPrefsAdapter.fetchMostCurrentEmail(retrieveCustomerAgreement, internetRegistrationResponse);
                this.paymentItem = this.easyPayAdapter.getPaymentItem(automaticPayments);
                this.automaticPaymentSuspended = this.easyPayAdapter.isRecurringPaymentSuspended(retrieveCustomerAgreement);
                const address: AccountPolicyHolderAddressElements = this.managePaymentPrefsAdapter.addressFromInternetRegistrationResponse(internetRegistrationResponse);
                this.billingStreet1 = address?.AcctPolHolderAddr1;
                this.billingCity = address?.AcctPolHolderCity;
                this.billingState = address?.AcctPolHolderState;
                this.paymentPlan = this.managePaymentPrefsAdapter.getPayPlanValueForEBI(retrieveCustomerAgreement);
                this.celebrusTrackingService.storePaymentPreferenceData({ paymentPlan: this.paymentPlan });
                this.canPayNow = this.managePaymentPrefsAdapter.canPay(billingAgreement.roleInAgreement.permission);
                this.isFullPay = this.managePaymentPrefsAdapter.isAccountFullPay(retrieveCustomerAgreement);
                this.paymentMethod = this.convertToPaymentMethod(automaticPayments);
                this.canCancelOrSuspendAutomaticPayments = this.payPlanAdapter.canCancelOrSuspendAutomaticPayments(paymentsHistory?.payments);
                const parsedAmountDue = Number((this.amountDue || '').replace(/[^0-9.-]/g, ''));
                this.isPaymentProcessingWindow = this.payPlanAdapter.isPaymentProcessingWindow(paymentsHistory?.payments, parsedAmountDue);
                this.initSelectedPayMethod();
                this.setupEditPaymentForm();

                this.handleVulError();
                this.loadComplete.set(true);
                this.apiCallInProgress.set(false);
                this.sendOnPageLoadEbiEvent();
            });
        } catch (error) {
            this.logger.error('API ERROR: EDIT AUTOMATIC PAYMENTS LANDING PAGE', error);
        }
    }
    navigateToReview(): void {
        this.router.navigateByUrl(
            environment.ISB.ENDPOINTS.MANAGE_PAYMENT_PREFERENCES.AUTOMATIC_PAYMENTS.EDIT.REVIEW(
                this.removeParamFromUrl(this.urlUtil.hashParamsString, 'review')
            )
        );
    }

    removeParamFromUrl(urlParameters: string, parameterName: string): string {
        return urlParameters.includes(parameterName) ?
            urlParameters.replace('&fromPage=review', '') :
            urlParameters;
    }

    setupEditPaymentForm(): void {
        const fromPage: string = this.activatedRoute.snapshot.queryParams['fromPage'];
        if (this.editPaymentFlow.flowInPlay ||
            this.editPaymentFlow.hasCreditCardVulPolicyError && fromPage && fromPage === 'review') {
            this.editPaymentForm = this.editPaymentFormBuilder.setupEasyPayReconstructFormGroup({
                oldForm: this.editPaymentFlow.editAutomaticPaymentForm.group
            });
        } else {
            this.editPaymentForm = this.editPaymentFormBuilder.newAutomaticPaymentFormGroup();
        }
        this.editPaymentFlow.editAutomaticPaymentForm = this.editPaymentForm;
    }

    onContinue(): void {
        this.formUtils.touchAllFormControls(this.editPaymentForm.group);
        this.formValidated = !!this.isAccountValidationReturnErrorCode;
        this.setDefaultPaymentMethodSelectionError(this.selectedPaymentMethod);
        if (this.editPaymentForm.group.valid) {
            this.sendOnPaymentMethodEditEbiEvent();
            this.determineNextAction();
        } else {
            this.window.scroll(0, 0);
        }
    }

    saveFormToFlow(): void {
        this.editPaymentFlow.save({
            editAutomaticPaymentForm: this.editPaymentForm,
            billedStatus: this.isEFTBilled || this.isRBCBilled || this.isBilledStatus,
            emailAddress: this.emailAddress,
            selectedPayMethod: this.chosenPaymentMethod,
            originalPaymentMethod: this.paymentMethod,
            billingAccountHolderName: this.billingAccountHolderName,
            billingAccountNumber: this.billingAccountNumber,
            amountDue: this.amountDue,
            currentBillDueDate: this.currentBillDueDate,
            nextBillDueDate: this.nextBillDueDate,
            currentBillingMethodType: this.automaticPayments.billingMethodType,
            automaticPaymentSuspended: this.automaticPaymentSuspended,
            billingStreet1: this.billingStreet1,
            billingCity: this.billingCity,
            billingState: this.billingState,
            distributedDocumentsResponseModel: this.distributedDocumentsResponseModel,
            authorizationAgreementParams: this.authorizationAgreementParams,
            isLifeOnlyAccount: this.isLifeOnlyAccount,
            canPayNow: this.canPayNow,
            hasCreditCardVulPolicyError: false,
            paymentPlan: this.paymentPlan,
            canCancelOrSuspendAutomaticPayments: this.canCancelOrSuspendAutomaticPayments
        });
    }

    initSelectedPayMethod(): void {
        this.chosenPaymentMethod = new PaymentItem(this.paymentMethod.item);
    }

    cancelEditPayment(): void {
        this.router.navigateByUrl(
            environment.ISB.ENDPOINTS.MANAGE_PAYMENT_PREFERENCES.paymentPreferences(this.urlUtil.hashParamsString)
        );
    }

    handleVulError(): void {
        this.hasCreditCardVulPolicyError.set(this.editPaymentFlow.hasCreditCardVulPolicyError || this.hasCreditCardVulPolicyError());
        this.vulErrorMessage.set(this.editPaymentFlow.errorDetails?.errorMessage || this.vulErrorMessage());
    }

    sendOnPageLoadEbiEvent(): void {
        this.eventService.handleEvent(EBIEventType.ON_LOAD_EDIT_AUTOMATIC_PAYMENTS_LANDING_PAGE, {
            paymentMethod: this.ebiPaymentMethodData,
            paymentPlan: this.paymentPlan || this.editPaymentFlow.paymentPlan
        });
    }

    sendOnPaymentMethodEditEbiEvent(): void {
        this.eventService.handleEvent(EBIEventType.ON_EDIT_OF_PAYMENT_METHOD_ON_AUTOMATIC_PAYMENT_LANDING_PAGE, {
            paymentMethod: this.ebiPaymentMethodData,
            paymentPlan: this.paymentPlan || this.editPaymentFlow.paymentPlan
        });
    }

    convertToPaymentMethod(automaticPayments: IsbExperience.GetPayPlanResponse): PaymentItem {
        const paymentMethod = automaticPayments.paymentMethod;
        const paymentMethodType = automaticPayments.paymentMethod.paymentMethodType;
        const lastFourDigits = 4;
        if (paymentMethodType === 'ElectronicFundsTransfer.PaymentMethod') {
            return new PaymentItem(new BankAccountPaymentItem(
                paymentMethod.payorInfo.firstName,
                paymentMethod.payorInfo.lastName,
                paymentMethod.payorInfo.middleName,
                '',
                '',
                '',
                '',
                '',
                paymentMethod.electronicFundsTransfer.bankName,
                this.paymentMethodsAdapter.retrieveBankAccountType(paymentMethod.electronicFundsTransfer.bankAccountType),
                paymentMethod.electronicFundsTransfer.bankRoutingNumber,
                paymentMethod.electronicFundsTransfer.bankAccountNumber.slice(paymentMethod.electronicFundsTransfer.bankAccountNumber.length - lastFourDigits),
                '',
                paymentMethod.payorInfo.fullName
            )
            );
        } else if (paymentMethodType === 'BankCard.PaymentMethod') {
            return new PaymentItem(new BankCardPaymentItem(
                paymentMethod.payorInfo.firstName,
                paymentMethod.payorInfo.lastName,
                paymentMethod.payorInfo.middleName,
                paymentMethod.bankCard.cvvCode,
                paymentMethod.payorInfo.postalCode,
                this.paymentMethodsAdapter.retrieveBankCardType(paymentMethod.bankCard.cardBrand),
                paymentMethod.bankCard.ccLastFour,
                this.paymentMethodsAdapter.retrieveBankCardExpirationDate(paymentMethod.bankCard.expirationDate),
                paymentMethod.bankCard.ccLastFour
            )
            );
        }
    }

    determineNextAction(): void {
        this.commonValuesPaymentFormGroupModel = new CommonValuesPaymentFormGroupModel(this.editPaymentForm, this.selectedPaymentMethod, this.billingAccountHolderName);
        this.setAuthorizationAgreementParams(false);
        if (this.selectedPaymentMethod?.isPlaceholder) {
            this.isGlobalErrorMessageReqDefaultMethod = true;
        } else {
            this.saveFormToFlow();
            this.navigateToReview();
        }
    }

    get showChangeNotEffectiveWarning(): boolean {
        const hasCurrentBill = !!this.NBP && this.hasCurrentBillBalance;
        const hasMethodChange = this.isPayMethodTypeChanged || !this.canCancelOrSuspendAutomaticPayments;
        return hasCurrentBill && (hasMethodChange || this.isPaymentProcessingWindow);
    }

    get hasCurrentBillBalance(): boolean {
        const amount = Number((this.amountDue || '').toString().replace(/[^0-9.-]/g, ''));
        return !isNaN(amount) && amount > 0;
    }

    get isPaymentMethodChanged(): boolean {
        const lastFourBankAccount = -4;
        if (this.isPayMethodTypeChanged) {
            return true;
        }

        if (this.getCurrentBillingMethodType().isRecurringBankcard) {
            return this.automaticPayments.paymentMethod.bankCard.ccLastFour !== this.selectedPaymentMethod.displayNumbers;
        } else {
            return this.automaticPayments.paymentMethod.electronicFundsTransfer.bankAccountNumber.slice(lastFourBankAccount) !== this.selectedPaymentMethod.maskedAccountNumbers.slice(lastFourBankAccount);
        }
    }

    get todayDisplayDate(): string {
        return this.easternTime.transform(new Date(), 'MM/dd/yyyy');
    }

    // Detect RBC <-> REFT change via selected payment method
    get isPayMethodTypeChanged(): boolean {
        const billingMethodType = this.getCurrentBillingMethodType();
        const selectedIsBankCard = !!this.selectedPaymentMethod?.isBankCard;
        const selectedIsBankAccount = !!this.selectedPaymentMethod?.isBankAccount;
        return billingMethodType.isRecurringBankcard && selectedIsBankAccount || billingMethodType.isRecurringEFT && selectedIsBankCard;
    }

    // Info display logic: NBP accounts only; same method type selection guarded by RBC/REFT + not changed
    get showChangeEffectiveImmediately(): boolean {
        const billingMethodType = this.getCurrentBillingMethodType();
        return !!this.NBP && this.hasCurrentBillBalance && !this.isPaymentProcessingWindow && (billingMethodType.isRecurringBankcard || billingMethodType.isRecurringEFT) && !this.isPayMethodTypeChanged && this.canCancelOrSuspendAutomaticPayments && this.isPaymentMethodChanged;
    }

    private getCurrentBillingMethodType(): { isRecurringBankcard: boolean; isRecurringEFT: boolean } {
        const currentType = (this.automaticPayments?.billingMethodType || '').toLowerCase();
        return {
            isRecurringBankcard: currentType === 'recurring bankcard',
            isRecurringEFT: currentType === 'recurring eft'
        };
    }

    retrieveBankCardType(cardName: string): string {
        return this.paymentMethodsAdapter.retrieveBankCardType(cardName);
    }

    get ebiPaymentMethodData(): PaymentMethodsData[] {
        const data = [];
        const payMethod = this.selectedPaymentMethod;
        const payPlan = this.automaticPayments;
        const accountType = payMethod.isBankAccount || !payMethod.isNewMethod && (payMethod.item as BankAccountPaymentItem).accountType ? (payMethod.item as BankAccountPaymentItem).accountType : '';
        const bankName = (payMethod.item as BankAccountPaymentItem).bankName || '';
        const creditCardVendor = payMethod.isBankCard || !payMethod.isNewMethod && this.automaticPayments.paymentMethod?.bankCard ? (payMethod.item as BankCardPaymentItem).cardType || this.retrieveBankCardType(payPlan.paymentMethod.bankCard.cardBrand) || this.automaticPayments.paymentMethod.bankCard.cardBrand : '';
        const paymentType = payMethod.isBankCard ? 'CC' : 'EFT';
        const paymentDate = this.showChangeEffectiveImmediately ? this.currentBillingPaymentDate : this.nextBillingPaymentDate;
        const nextPaymentAmount = this.amountDue;
        const stateEbi = this.editPaymentForm.state;
        data.push({
            accountType,
            bankName,
            creditCardVendor,
            paymentType,
            paymentDate,
            nextPaymentAmount,
            stateEbi
        });

        return data;
    }

    get currentBillingPaymentDate(): string {
        return this.currentBillDueDate ? this.easternTime.transform(this.currentBillDueDate, 'MM/dd/yyyy') : 'NA';
    }

    get nextBillingPaymentDate(): string {
        return this.nextBillDueDate ? this.easternTime.transform(this.nextBillDueDate, 'MM/dd/yyyy') : 'NA';
    }

    get selectedPaymentMethod(): PaymentItem {
        return this.chosenPaymentMethod;
    }

    set selectedPaymentMethod(paymentMethod: PaymentItem) {
        this.chosenPaymentMethod = paymentMethod;
    }

    get globalErrorDisplayable(): boolean {
        return !this.formValidated && !this.editPaymentForm.group.valid &&
            this.submitted || this.isGlobalErrorMessageReqDefaultMethod;
    }

    get submitted(): boolean {
        return this.editPaymentFormDirective && this.editPaymentFormDirective.submitted;
    }

    get considerThisGroup(): AbstractControl {
        return this.editPaymentForm && this.editPaymentForm.group.get('considerThisGroup');
    }

    private fetchBankCardAutomaticPaymentsCapMessage(): void {
        const contentfulResponse = this.contentfulService.getContentfulBillingContent();
        contentfulResponse.subscribe((res) => {
            this.bankCardAutomaticPaymentsCapMessage = this.session.isSAP ? res.sapbankCardAutomaticPaymentsCapMessage.content : res.bankCardAutomaticPaymentsCapMessage.content;
        });
    }

    private hasDefaultMethod(paymentMethods: PaymentItem[]): boolean {
        return !!paymentMethods.find((method) => method.isDefault);
    }

    private hasSavedPaymentMethod(paymentMethods: PaymentItem[]): boolean {
        return !!paymentMethods.find((method) => !method.isNewMethod && !method.isPlaceholder);
    }
}
